import React, { useEffect, useState } from "react";
import {
  Box,
  ButtonStyled,
  DataContainerHeader,
  FormElementWrapper,
  LabelStyled,
  SelectionDropdown,
  SeparatorBorder,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SettingsWrapper, FormDataContainer } from "./styled";
import { useNavigate } from "react-router-dom";
import ValidatedInput from "../../../components/common/inputFields/ValidatedInput";
import ImageUpload from "../../../components/common/uploadFiles/imagesUpload";
import EstablishmentApi from "../../../services/api/establishmentApi";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Form } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import ValidationController from "../../../components/common/inputFields/ValidationController";
import ValidationErrorText from "../../../components/common/inputFields/ValidationErrorText";
import { useUser } from "../../../context/userContext";
import PhoneInputField from "../../../components/common/inputFields/PhoneInput";
import { LazyLoadDropdown } from "../../../components/common/dropdowns/LazyLoadDropdown";
import HQBranchesApi from "../../../services/api/hqBranchesApi";

const EditEstablishment = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    setValue, // For setting dropdown values programmatically
    control,
    reset,
    formState: { errors },
  } = useForm();
  const { fetchUserInfo } = useUser();

  // State for dropdowns
  const [typeOptions, setTypeOptions] = useState([]); // Type options from API
  const [sectorOptions, setSectorOptions] = useState([]); // Sector options from API
  const [countries, setCountries] = useState([]);
  const selectedCountry = watch("country_id");
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [logoDelete, setLogoDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const location = useLocation();
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const handleSearch = (searchQuery) => {
    fetchCities(selectedCountry, currentPage, searchQuery);
  };

  const handleLoadMore = () => {
    fetchCities(selectedCountry, currentPage + 1);
  };

  // Fetch data from the API
  const fetchTypeOptions = async () => {
    try {
      const response = await EstablishmentApi.editEstablishment();

      if (response?.data?.organization_type) {
        const mappedTypeOptions = response.data.organization_type.map(
          (type) => ({
            label: type.name, // Use the name from the API response
            value: type.id,
          })
        );
        setTypeOptions(mappedTypeOptions);
      }

      // Map options for Type
      if (response?.data?.sectors) {
        const mappedSectorOptions = response.data.sectors.map((sector) => ({
          label: sector.name, // Use the name from the API response
          value: sector.id,
        }));
        setSectorOptions(mappedSectorOptions);
      }
    } catch (error) {}
  };

  const fetchCountries = async () => {
    try {
      const response = await EstablishmentApi.countyList();
      if (response.status) {
        const countryOptions = response.data.map((country) => ({
          value: country.id,
          label: `${language === "ar" ? country.ar_name : country.en_name} ${
            country.emoji
          }`,
        }));
        setCountries(countryOptions);
      }
    } catch (error) {}
  };

  const fetchCities = async (countryId, pageNum = 1, searchQuery) => {
    if (citiesLoading) return;
    try {
      setCitiesLoading(true);
      const response = await HQBranchesApi.getCitiesFromCountry(
        countryId,
        pageNum,
        searchQuery
      );
      if (response.status) {
        const cityOptions = response.data.data.map((city) => ({
          label: language === "ar" ? city.ar_name : city.en_name,
          value: city.id,
        }));
        // setCities(cityOptions);
        if (searchQuery) {
          setFilteredCities(cityOptions);
        } else {
          setCities((prevCities) => [...prevCities, ...cityOptions]);
        }

        setHasMore(!!response.data.next_page_url);
        setCurrentPage(pageNum);
      }
    } catch (error) {
    } finally {
      setCitiesLoading(false);
    }
  };

  const getEstablishmentDetails = async () => {
    try {
      const response = await EstablishmentApi.establishment();
      if (response?.data) {
        reset({
          organization_type_id: response?.data?.oragnization_type?.id || "",
          sector: response?.data?.sector || "",
          organization_name: response?.data?.organization_name || "",
          description: response?.data?.description || "",
          country_id: response?.data?.country?.id || "",
          city_id: response?.data?.city_id || "",
          address: response?.data?.address || "",
          contact_phone: response?.data?.contact_phone || "",
          contact_email: response?.data?.contact_email || "",
          contact_person: response?.data?.contact_person,
          // logo: response?.data?.logo,
        });

        const selectedCity = {
          label: response?.data?.country?.city,
          value: response?.data?.city_id,
        };
        if (
          cities &&
          !cities.some((city) => city?.id === selectedCity?.value)
        ) {
          setCities((prevCities) => [...prevCities, selectedCity]);
        }

        const baseAssetURL = process.env.REACT_APP_ASSET_URL;
        const logoUrl = response?.data?.logo
          ? `${baseAssetURL}/${response.data.logo}`
          : null;
        setLogoFile(logoUrl);
      }
    } catch (error) {
    } finally {
    }
  };

  const manageLoading = async (asyncFunctions) => {
    try {
      setDataLoading(true);
      await Promise.all(asyncFunctions.map((fn) => fn()));
    } catch (error) {
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    manageLoading([fetchTypeOptions, fetchCountries, getEstablishmentDetails]);
  }, [language]);

  // Fetch cities when country changes
  useEffect(() => {
    if (selectedCountry) {
      setCities([]); // Clear cities when a new country is selected
      fetchCities(selectedCountry); // Fetch cities for the selected country
    }
  }, [selectedCountry, language]);

  const onSubmit = async (formData) => {
    try {
      setLoading(true);

      // Create a FormData object
      const updatedData = new FormData();
      updatedData.append("action", "create");
      updatedData.append("logo_delete", logoDelete);

      // Append form fields to FormData
      Object.entries(formData).forEach(([key, value]) => {
        if (!value) {
          updatedData.delete(key);
        } else updatedData.append(key, value);
      });

      const response = await EstablishmentApi.updateEstablishment(updatedData);
      if (response.code === 200) {
        toast.success(t("establishment_updated"));
        fetchUserInfo();
        reset();
        navigate("/settings/establishment");
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingsWrapper>
      <Box className="content-head">
        <SubHeadingStyled size="16px" color="var(--text)">
          {t("settings")}
        </SubHeadingStyled>
      </Box>
      {dataLoading ? (
        <ThemeLoader />
      ) : (
        <Box className="content-container">
          <Row className="mt-3">
            <Col md={12}>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormDataContainer className="dataContainer">
                  <DataContainerHeader
                    backBtnLabel={t("establishment")}
                    noBreadcrumbs=""
                    onClickBack={() => navigate("/settings/establishment")}
                  />
                  <FormElementWrapper className="combined pt-3">
                    <Box className="formEleContainer">
                      <LabelStyled
                        className="labelForm"
                        size="15px"
                        color="var(--text)"
                      >
                        {t("type")}
                        <span className="staric">*</span>
                      </LabelStyled>
                      <Box className="w-100">
                        <SelectionDropdown
                          name="organization_type_id"
                          placeholder={t("select_type")}
                          options={typeOptions}
                          value={watch("organization_type_id")}
                          register={register}
                          errors={errors}
                          validationRules={{
                            required: true,
                          }}
                        />
                        {errors.organization_type_id?.type === "required" && (
                          <ValidationErrorText
                            text={
                              t(errors.organization_type_id?.message) ||
                              t("Pleaseselectone")
                            }
                          />
                        )}
                      </Box>
                    </Box>
                  </FormElementWrapper>
                  <ValidatedInput
                    name="sector"
                    type="input"
                    fieldType="text"
                    placeholder={t("Sector")}
                    label="Sector"
                    className="combined"
                    staric="staric"
                    register={register}
                    errors={errors}
                    validationRules={{
                      required: true,
                    }}
                  />
                  {/* <FormElementWrapper className="combined">
                    <Box className="formEleContainer">
                      <LabelStyled
                        className="labelForm"
                        size="15px"
                        color="var(--text)"
                      >
                        {t("Sector")}
                        <span className="staric">*</span>
                      </LabelStyled>
                      <Box className="w-100">
                        <SelectionDropdown
                          name="sector_id"
                          placeholder={t("select_sector")}
                          options={sectorOptions} // Pass the fetched options
                          value={watch("sector_id")}
                          register={register}
                          errors={errors}
                          validationRules={{
                            required: true,
                          }}
                        />
                        {errors.sector_id?.type === "required" && (
                          <ValidationErrorText
                            text={
                              t(errors.sector_id?.message) ||
                              t("Pleaseselectone")
                            }
                          />
                        )}
                      </Box>
                    </Box>
                  </FormElementWrapper> */}
                  <ValidatedInput
                    name="organization_name"
                    type="input"
                    fieldType="text"
                    placeholder={t("name")}
                    label="name"
                    className="combined"
                    staric="staric"
                    register={register}
                    errors={errors}
                    validationRules={{
                      required: true,
                    }}
                  />
                  <ValidatedInput
                    name="description"
                    type="textarea"
                    fieldType="text"
                    placeholder={"Description"}
                    label="Description"
                    className="combined"
                    errors={errors}
                    register={register}
                    validationRules={{
                      required: false,
                    }}
                  />
                  <SeparatorBorder className="mt-2" />
                  <Box className="innerFoHead">
                    <SubHeadingStyled size="16px" color="var(--text)">
                      {t("location")}
                    </SubHeadingStyled>
                  </Box>
                  <FormElementWrapper className="combined pt-3">
                    <Box className="formEleContainer">
                      <LabelStyled
                        className="labelForm"
                        size="15px"
                        color="var(--text)"
                      >
                        {t("Country")}
                        {/* <span className="staric">*</span> */}
                      </LabelStyled>
                      <Box className="w-100">
                        <SelectionDropdown
                          name="country_id"
                          className="countryDropdown"
                          placeholder={t("select_country")}
                          options={countries}
                          value={watch("country_id")}
                          register={register}
                          errors={errors}
                          validationRules={{
                            required: false,
                          }}
                        />
                        {errors.country_id?.type === "required" && (
                          <ValidationErrorText
                            text={
                              t(errors.country_id?.message) ||
                              t("Pleaseselectone")
                            }
                          />
                        )}
                      </Box>
                    </Box>
                  </FormElementWrapper>
                  <FormElementWrapper className="combined">
                    <Box className="formEleContainer">
                      <LabelStyled
                        className="labelForm"
                        size="15px"
                        color="var(--text)"
                      >
                        {t("City")}
                        {/* <span className="staric">*</span> */}
                      </LabelStyled>
                      <Box className="w-100">
                        <LazyLoadDropdown
                          name="city_id"
                          control={control}
                          label={t("name_en")}
                          placeholder={t("select_city")}
                          options={cities}
                          filteredOptions={filteredCities}
                          value={watch("city_id")}
                          register={register}
                          loading={citiesLoading}
                          hasMore={hasMore}
                          onSearch={handleSearch}
                          onLoadMore={handleLoadMore}
                          errors={errors}
                          validationRules={{ required: false }}
                        />
                      </Box>
                    </Box>
                  </FormElementWrapper>
                  <ValidatedInput
                    name="address"
                    type="input"
                    fieldType="text"
                    placeholder={"AddressFull"}
                    label="Address"
                    className="combined"
                    // staric={true}
                    errors={errors}
                    register={register}
                    validationRules={{
                      required: false,
                    }}
                  />
                  <SeparatorBorder className="mt-2" />
                  <Box className="innerFoHead">
                    <SubHeadingStyled size="16px" color="var(--text)">
                      {t("ContactDetails")}
                    </SubHeadingStyled>
                  </Box>
                  <PhoneInputField
                    name="contact_phone"
                    label={"contact_no"}
                    placeholder={"contact_no"}
                    // staric="staric"
                    className={"border-0"}
                    register={register}
                    control={control}
                    errors={errors}
                    validationRules={{
                      required: false,
                    }}
                  />
                  <ValidatedInput
                    name="contact_email"
                    type="input"
                    fieldType="text"
                    placeholder={t("Email")}
                    label="Email"
                    className="combined"
                    // staric="staric"
                    errors={errors}
                    register={register}
                    validationRules={{
                      required: false,
                    }}
                  />
                  <ValidatedInput
                    name="contact_person"
                    type="input"
                    fieldType="text"
                    placeholder={t("ContactPerson")}
                    label="ContactPerson"
                    // staric={true}
                    className="combined"
                    errors={errors}
                    register={register}
                    validationRules={{
                      required: false,
                    }}
                  />
                  <SeparatorBorder className="mt-2" />
                  <FormElementWrapper className="combined pt-3">
                    <Box className="formEleContainer">
                      <LabelStyled
                        className="labelForm"
                        size="15px"
                        color="var(--text)"
                      >
                        {t("UploadLogo")}
                      </LabelStyled>
                      <ValidationController
                        name="logo"
                        validationConfig={{
                          required: false,
                        }}
                        errors={errors}
                        control={control}
                        renderer={({ field }) => (
                          <ImageUpload
                            UploadImage="UploadLogo"
                            setLogoDelete={setLogoDelete}
                            onFileSelect={(file) => {
                              field.onChange(file); // Update form field
                              setLogoFile(file); // Update logo file state
                            }}
                            initialImage={logoFile} // Pass the preloaded image URL
                          />
                        )}
                      />
                      {/* <ImageUpload
                                                UploadImage="UploadLogo"
                                                
                                                onFileSelect={field.onChange} // Pass react-hook-form's onChange
                                            /> */}
                    </Box>
                  </FormElementWrapper>

                  <SeparatorBorder className="mt-2" />
                  <Box className="commonBtns w-100">
                    <ButtonStyled
                      bg="var(--bgSecondary)"
                      label={t("Cancel")}
                      color="var(--text2)"
                      type="button"
                      onClick={() => navigate("/settings/establishment")}
                    ></ButtonStyled>
                    <ButtonStyled
                      label={t("Save")}
                      type="submit"
                      isLoading={loading}
                    ></ButtonStyled>
                  </Box>
                </FormDataContainer>
              </Form>
            </Col>
          </Row>
        </Box>
      )}
    </SettingsWrapper>
  );
};

export default EditEstablishment;
